import { useEffect, useMemo, useState } from "react";
import { PublicHeader, Footer } from "../App";
import { Doc } from "../Doc";
import { useT } from "../i18n";

/**
 * Developers (PLAN.md 4.16 K6): the Vrelis API for service accounts, rendered from
 * /api/v1/openapi.json, the document the server builds from the same definitions that mount the
 * routes. Our own renderer instead of Swagger UI: it runs under the site's CSP (script-src and
 * style-src 'self'), follows the theme and fits 375 px. "Try it" keeps the token in memory only.
 */

type Schema = { $ref?: string; [key: string]: unknown };
type Parameter = { name: string; in: "path" | "query"; required?: boolean; description?: string; schema?: Schema };
type Operation = {
  summary?: string;
  description?: string;
  tags?: string[];
  parameters?: Parameter[];
  requestBody?: { content: Record<string, { schema: Schema }> };
  responses: Record<string, { description: string; content?: Record<string, { schema: Schema }> }>;
};
type OpenApi = { info: { title: string; version: string; description?: string }; servers?: { url: string }[]; paths: Record<string, Record<string, Operation>>; components?: { schemas?: Record<string, Schema> } };

const METHODS = ["get", "post", "put", "delete"] as const;

const SDK_EXAMPLE = `import { createQuicknetClient, roundForTime, sealPrivate, secretKeyFromPhrase } from "@timecapsule/crypto";

// The service's own key, from the 12 words made under Account, Keys. Keep them in a secret store.
const secretKey = secretKeyFromPhrase(process.env.VRELIS_PHRASE!);
const api = (path: string, init: RequestInit = {}) =>
  fetch(\`https://vrelis.com/api/v1\${path}\`, { ...init, headers: { authorization: \`Bearer \${process.env.VRELIS_TOKEN}\`, "content-type": "application/json" } });

// A member you follow, found by name (at least 2 letters); the capsule is sealed to their active key.
const { members } = await (await api("/members?q=Ana")).json();
const ana = members.find((m: { display_name: string }) => m.display_name === "Ana");

const event = await sealPrivate({
  subject: "For next year",                       // never encrypted
  intro: "A letter for your birthday.",           // optional; the recipient reads it before unlock
  plaintext: "Opened only after its time.",
  round: roundForTime(Date.now() + 365 * 86_400_000),
  secretKey,
  recipientPublicKey: ana.pubkey,
  chainClient: createQuicknetClient(),
});
const sent = await api("/capsules", { method: "POST", body: JSON.stringify({ event }) });
console.log(sent.status, await sent.json());`;

export function Developers() {
  const t = useT();
  const [doc, setDoc] = useState<OpenApi | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [token, setToken] = useState("");
  useEffect(() => {
    fetch("/api/v1/openapi.json").then((r) => r.json()).then(setDoc, (err) => setError(String(err)));
  }, []);

  const groups = useMemo(() => {
    const out = new Map<string, { method: string; path: string; op: Operation }[]>();
    for (const [path, item] of Object.entries(doc?.paths ?? {})) {
      for (const method of METHODS) {
        const op = item[method];
        if (!op) continue;
        const tag = op.tags?.[0] ?? "API";
        out.set(tag, [...(out.get(tag) ?? []), { method, path, op }]);
      }
    }
    return [...out.entries()];
  }, [doc]);

  return (
    <>
      <PublicHeader />
      <Doc>
        <h1>{t.developersPage.title}</h1>
        <p className="lead">{t.developersPage.lead}</p>

        <h2>{t.developersPage.accessTitle}</h2>
        <ol>{t.developersPage.access.map((line, i) => <li key={i}>{line}</li>)}</ol>

        <h2>{t.developersPage.securityTitle}</h2>
        <ul>{t.developersPage.security.map((line, i) => <li key={i}>{line}</li>)}</ul>

        <h2>{t.developersPage.limitsTitle}</h2>
        <ul>{t.developersPage.limits.map((line, i) => <li key={i}>{line}</li>)}</ul>

        <h2>{t.developersPage.sdkTitle}</h2>
        <p>{t.developersPage.sdkText}</p>
        <pre className="code-block"><code>{SDK_EXAMPLE}</code></pre>

        <h2>{t.developersPage.referenceTitle}</h2>
        <p className="small">
          {t.developersPage.referenceText} <a href="/api/v1/openapi.json">openapi.json</a>
        </p>
        <label className="small">
          {t.developersPage.tokenLabel}
          <input type="password" autoComplete="off" value={token} onChange={(e) => setToken(e.target.value.trim())} placeholder="vrl_…" />
        </label>
        <p className="small muted">{t.developersPage.tokenNote}</p>
        {error && <p className="error">{error}</p>}
        {!doc && !error && <p className="muted">{t.common.loading}</p>}
        {groups.map(([tag, ops]) => (
          <section key={tag} className="api-group">
            <h3>{tag}</h3>
            {ops.map(({ method, path, op }) => <Endpoint key={method + path} method={method} path={path} op={op} token={token} />)}
          </section>
        ))}
        {doc?.components?.schemas && (
          <section className="api-group">
            <h3>{t.developersPage.schemas}</h3>
            {Object.entries(doc.components.schemas).map(([name, schema]) => (
              <details key={name} className="api-endpoint">
                <summary><code>{name}</code></summary>
                <pre className="code-block"><code>{JSON.stringify(schema, null, 2)}</code></pre>
              </details>
            ))}
          </section>
        )}
      </Doc>
      <Footer />
    </>
  );
}

function schemaText(schema: Schema | undefined): string {
  return schema ? JSON.stringify(schema, null, 2) : "";
}

function Endpoint({ method, path, op, token }: { method: string; path: string; op: Operation; token: string }) {
  const t = useT();
  const params = op.parameters ?? [];
  const jsonBody = op.requestBody?.content["application/json"]?.schema;
  const binaryBody = !!op.requestBody?.content["application/octet-stream"];
  const [values, setValues] = useState<Record<string, string>>({});
  const [body, setBody] = useState("");
  const [result, setResult] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const ref = (s?: Schema) => (s?.$ref ? s.$ref.split("/").pop() : null);

  async function send() {
    setBusy(true);
    setResult(null);
    try {
      let url = `/api/v1${path.replace(/\{(\w+)\}/g, (_, name: string) => encodeURIComponent(values[name] ?? ""))}`;
      const query = params.filter((p) => p.in === "query" && values[p.name]).map((p) => `${p.name}=${encodeURIComponent(values[p.name]!)}`);
      if (query.length) url += `?${query.join("&")}`;
      const res = await fetch(url, {
        method: method.toUpperCase(),
        credentials: "omit",
        headers: { authorization: `Bearer ${token}`, ...(jsonBody ? { "content-type": "application/json" } : {}) },
        body: jsonBody && body.trim() ? body : undefined,
      });
      const text = await res.text();
      let pretty = text;
      try { pretty = JSON.stringify(JSON.parse(text), null, 2); } catch { /* not JSON */ }
      setResult(`${res.status} ${res.statusText}\n\n${pretty}`);
    } catch (err) {
      setResult(String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="api-endpoint">
      <summary>
        <span className={`method method-${method}`}>{method.toUpperCase()}</span> <code>{path}</code> <span className="muted small">{op.summary}</span>
      </summary>
      {op.description && <p className="small pre-line">{op.description}</p>}
      {params.length > 0 && (
        <table className="small api-params">
          <thead><tr><th>{t.developersPage.parameter}</th><th>{t.developersPage.where}</th><th>{t.developersPage.description}</th></tr></thead>
          <tbody>
            {params.map((p) => (
              <tr key={p.in + p.name}>
                <td><code>{p.name}</code>{p.required ? " *" : ""}</td>
                <td>{p.in}</td>
                <td>{p.description ?? ref(p.schema) ?? (p.schema?.type as string | undefined) ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {jsonBody && (
        <>
          <p className="small">{t.developersPage.body}</p>
          <pre className="code-block"><code>{schemaText(jsonBody)}</code></pre>
        </>
      )}
      {binaryBody && <p className="small">{t.developersPage.binaryBody}</p>}
      <p className="small">{t.developersPage.responses}</p>
      <ul className="small">
        {Object.entries(op.responses).map(([code, r]) => {
          const schema = r.content ? Object.values(r.content)[0]?.schema : undefined;
          return <li key={code}><code>{code}</code> {r.description}{schema ? <> → <code>{ref(schema) ?? (schema.type as string | undefined) ?? "object"}</code></> : null}</li>;
        })}
      </ul>
      <div className="api-try">
        <p className="small"><b>{t.developersPage.tryIt}</b></p>
        {params.map((p) => (
          <label key={p.in + p.name} className="small">
            {p.name}
            <input value={values[p.name] ?? ""} onChange={(e) => setValues({ ...values, [p.name]: e.target.value })} />
          </label>
        ))}
        {jsonBody && <textarea rows={5} className="small" placeholder='{"event": {...}}' value={body} onChange={(e) => setBody(e.target.value)} />}
        <button type="button" disabled={busy || !token || binaryBody} onClick={() => void send()}>{binaryBody ? t.developersPage.binaryNoTry : t.developersPage.send}</button>
        {result && <pre className="code-block"><code>{result}</code></pre>}
      </div>
    </details>
  );
}
